import React, { useState } from 'react';
import { Button, Card, Col, Form as ReactForm, Row } from 'react-bootstrap';
import { screeningClearanceRuleApi, screeningClearanceRulesAnalysisApi } from '../../../apis';

import {
  InternalApiAnalysisRunSampleType,
  InternalApiAnalysisRunStatus,
  InternalApiScreeningClearanceRule,
  InternalApiScreeningClearanceRulesAnalysis,
  InternalApiScreeningClearanceRulesAnalysisRun,
  InternalApiEntityType,
} from '../../../api-internal';
import { Form, Formik, FormikHelpers, FormikProps } from 'formik';
import InfoSection from '../../../form/InfoSection';
import { showFeedback } from '../../../feedback/feedbackReducer';
import { useDispatch } from 'react-redux';
import Table from '../../../table/redux/Table';
import FormattedDateTime from '../../../common/datetime/FormattedDateTime';
import { useNavigate } from 'react-router';
import { faPlay } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import './CreateScreeningClearanceRulesAnalysisRun.scss';
import FormField from '../../../form/FormField';
import * as Yup from 'yup';
import { Spinner } from '../../../feedback/Spinner';
import { DatePicker, SalvRadio, SalvRadioGroup, Size } from '@salvapp/ui-components';
import dayjs, { Dayjs } from 'dayjs';
import { parseErrorMessage } from '../../../common/errors/helpers';

interface Props {
  analysis: InternalApiScreeningClearanceRulesAnalysis;
  latestRun?: InternalApiScreeningClearanceRulesAnalysisRun;
  onCreate: () => void;
}

const validationSchema = Yup.object().shape({
  sampleType: Yup.string().required('Required'),
  sampleSize: Yup.number().when('sampleType', {
    is: InternalApiAnalysisRunSampleType.SET,
    then: (schema) => schema.required().max(100000),
  }),
  fromDate: Yup.date().when('sampleType', {
    is: InternalApiAnalysisRunSampleType.TIMEFRAME,
    then: (schema) => schema.required(),
  }),
  toDate: Yup.date().when('sampleType', {
    is: InternalApiAnalysisRunSampleType.TIMEFRAME,
    then: (schema) => schema.required(),
  }),
  skipFilteredAlerts: Yup.boolean(),
});

export interface ScreeningClearanceRulesAnalysisRunFormValues {
  note: string;
  sampleType: string;
  sampleSize?: number;
  fromDate: Dayjs | null;
  toDate: Dayjs | null;
  skipFilteredAlerts: boolean;
}

export function CreateScreeningClearanceRulesAnalysisRun({ analysis, onCreate, latestRun }: Props) {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [initializingRun, setInitializingRun] = useState(false);
  const currentDate = dayjs();
  const oneWeekAgo = dayjs().subtract(7, 'day');
  const initialValues: ScreeningClearanceRulesAnalysisRunFormValues = {
    note: '',
    sampleType: '',
    fromDate: oneWeekAgo,
    toDate: currentDate,
    skipFilteredAlerts: false,
  };
  const submit = async (
    formValues: ScreeningClearanceRulesAnalysisRunFormValues,
    { setStatus }: FormikHelpers<ScreeningClearanceRulesAnalysisRunFormValues>
  ) => {
    try {
      const run = {
        analysisId: analysis.id as number,
        note: formValues.note,
        skipFilteredAlerts: formValues.skipFilteredAlerts,
        sampleType: formValues.sampleType as InternalApiAnalysisRunSampleType,
      } as InternalApiScreeningClearanceRulesAnalysisRun;
      if (run.sampleType === InternalApiAnalysisRunSampleType.SET) {
        run.sampleSize = formValues.sampleSize;
      }
      if (run.sampleType === InternalApiAnalysisRunSampleType.TIMEFRAME) {
        run.fromDate = formValues.fromDate?.startOf('day').toDate();
        run.toDate = formValues.toDate?.endOf('day').toDate();
      }
      setInitializingRun(true);
      await screeningClearanceRulesAnalysisApi.addRun({
        analysisId: analysis.id as number,
        internalApiScreeningClearanceRulesAnalysisRun: run,
      });
      onCreate();
      setInitializingRun(false);
    } catch (e: any) {
      const errorMessage = await parseErrorMessage(e);
      setStatus(errorMessage);
      setInitializingRun(false);
      dispatch(
        showFeedback({
          message: errorMessage,
          success: false,
        })
      );
    }
  };

  const fetchScreeningClearanceRules = (page: number, size: number, entityType: InternalApiEntityType) => {
    return screeningClearanceRuleApi.findAll({ page, size, entityType });
  };

  const renderClearanceRules = (entityType: InternalApiEntityType) => {
    return (
      <Table
        title={'Clearance rules to be tested'}
        name={`tested-rules-${entityType}`}
        disableBoxShadow={true}
        fetchContent={(page, size) => fetchScreeningClearanceRules(page, size, entityType)}
        pageSize={10}
        header={() => (
          <tr>
            <th>Reason</th>
            <th>Date added</th>
            <th>Status</th>
          </tr>
        )}
        body={(rules: InternalApiScreeningClearanceRule[]) =>
          rules.length > 0 &&
          rules.map((rule) => (
            <tr
              key={rule.handle}
              onClick={() => navigate(`/screening/clearance-rules/${rule.handle}`)}
              className='clickable-row'
              data-testid='rule-row'
            >
              <td>{rule.reason}</td>
              <td>
                <FormattedDateTime date={rule.lastUpdatedTime} />
              </td>
              <td className={rule.enabled ? 'active' : 'inactive'}>{rule.enabled ? 'Active' : 'Inactive'}</td>
            </tr>
          ))
        }
      />
    );
  };

  const renderLoadingInfo = (infoText: string) => {
    return (
      <Card className={'card-info'}>
        <Card.Header>
          <h4 className='card-header-title'>Run analysis</h4>
        </Card.Header>
        <Card.Body>
          <Row className={'align-items-center ps-2'}>
            <Spinner loading={true} small={true} />
            <span className={'test-running m-2'}>{infoText}</span>
          </Row>
        </Card.Body>
      </Card>
    );
  };

  if (initializingRun) {
    return renderLoadingInfo('Initializing new run...');
  }

  if (
    latestRun &&
    (latestRun.status === InternalApiAnalysisRunStatus.RUNNING ||
      latestRun.status === InternalApiAnalysisRunStatus.SCHEDULED)
  ) {
    return renderLoadingInfo('Test is running. You can safely close the view while the test is running.');
  }

  return (
    <Card className={'card-info'}>
      <Card.Header>
        <h4 className='card-header-title'>Run analysis</h4>
      </Card.Header>
      <Card.Body>
        <Row>
          <Col>
            <Formik
              initialValues={initialValues}
              onSubmit={submit}
              validationSchema={validationSchema}
              validateOnChange={false}
              validateOnBlur={false}
            >
              {({ values, setFieldValue }: FormikProps<ScreeningClearanceRulesAnalysisRunFormValues>) => {
                return (
                  <Form id={'run-form'} data-testid={'run-form'}>
                    <h4 className={'mb-4'}>Define sample data</h4>
                    <SalvRadioGroup
                      size={Size.SMALL}
                      onChange={(value) => setFieldValue('sampleType', value)}
                      value={values.sampleType}
                    >
                      {latestRun && (
                        <Row className={'mb-3 ps-3'}>
                          <SalvRadio
                            value={InternalApiAnalysisRunSampleType.RECENT_RUN}
                            label='Same as most recent run'
                          />
                        </Row>
                      )}
                      <Row className={'mb-3 ps-3'}>
                        <div className='d-flex'>
                          <SalvRadio value={InternalApiAnalysisRunSampleType.TIMEFRAME} label='Timeframe' />
                          <SalvRadio value={InternalApiAnalysisRunSampleType.SET} label='Set of alerts' />
                        </div>
                      </Row>
                    </SalvRadioGroup>
                    {values.sampleType === InternalApiAnalysisRunSampleType.TIMEFRAME && (
                      <Col md={8} className={'p-0 timeframe-dates mb-4'}>
                        <ReactForm.Label>From</ReactForm.Label>
                        <DatePicker
                          value={values.fromDate}
                          onChange={(date: Dayjs | null) => {
                            setFieldValue('fromDate', date);
                          }}
                        />
                        <ReactForm.Label className={'mt-3'}>To</ReactForm.Label>
                        <DatePicker
                          value={values.toDate}
                          onChange={(date: Dayjs | null) => {
                            setFieldValue('toDate', date);
                          }}
                        />
                      </Col>
                    )}
                    {values.sampleType === InternalApiAnalysisRunSampleType.SET && (
                      <Row className={'ps-3 align-items-center'}>
                        <div className={'pe-2 set-sample-label'}>Last</div>
                        <FormField name={'sampleSize'} type='number' />
                        <div className={'ps-2 set-sample-label'}>{analysis.entityType.toLowerCase()} alerts</div>
                      </Row>
                    )}
                    <h4 className={'mb-3 mt-4'}>Alerts to include</h4>
                    <Row className={'ps-3 mb-4 align-items-center'}>
                      <div className={'ps-2 pe-3 set-sample-label'}>All</div>
                      <FormField
                        name={'skipFilteredAlerts'}
                        id={'skipFilteredAlerts'}
                        type='checkbox'
                        label={''}
                        className={'mb-0'}
                      />
                      <div className={'ps-2 set-sample-label'}>Exclude filtered</div>
                    </Row>
                    <InfoSection
                      editing={true}
                      label={'Note'}
                      type='textarea'
                      name={'note'}
                      placeholder={'What are you testing with this run?'}
                      value={values.note}
                    />
                  </Form>
                );
              }}
            </Formik>
          </Col>
          <Col md={8}>{renderClearanceRules(analysis.entityType)}</Col>
        </Row>
      </Card.Body>
      <Card.Footer>
        <Row>
          <Col className='d-flex align-items-center justify-content-end'>
            <Button type='submit' variant='primary' form='run-form'>
              <FontAwesomeIcon icon={faPlay} className='me-2' />
              Start run
            </Button>
          </Col>
        </Row>
      </Card.Footer>
    </Card>
  );
}

export default CreateScreeningClearanceRulesAnalysisRun;
